import { useTranslation } from "react-i18next";
import { type QuizTakersReport } from "~/api/types";
import { Badge } from "~/atoms/badge";
import { Card } from "~/atoms/card";
import { CenterSpinner } from "~/atoms/spinner";
import {
  Table,
  TableHeader,
  TableBody,
  TableHead,
  TableRow,
  TableCell,
} from "~/atoms/table";
import NoResult from "~/molecules/NoResult";
import ErrorState from "~/molecules/ErrorState";

interface Props {
  report: QuizTakersReport | undefined;
  isLoading: boolean;
  error: Error | undefined;
}

export const QuizTakersOverview = ({ report, isLoading, error }: Props) => {
  const { t } = useTranslation();

  if (isLoading) {
    return <CenterSpinner />;
  }

  if (error) {
    return <ErrorState />;
  }

  if (!report) {
    return <NoResult />;
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="grid grid-cols-2 gap-4">
        <Card className="p-4">
          <div className="text-muted-foreground text-sm">
            {t(($) => $.anchoring.manage.invited)}
          </div>
          <div className="text-2xl font-bold">{report.summary.invited}</div>
        </Card>
        <Card className="p-4">
          <div className="text-muted-foreground text-sm">
            {t(($) => $.anchoring.manage.completed)}
          </div>
          <div className="text-2xl font-bold">{report.summary.completed}</div>
        </Card>
      </div>

      <Card className="p-0">
        <Table className="table-fixed">
          <colgroup>
            <col className="w-auto" />
            <col className="w-28" />
          </colgroup>
          <TableHeader className="bg-muted">
            <TableRow>
              <TableHead>{t(($) => $.anchoring.manage.taker)}</TableHead>
              <TableHead className="whitespace-nowrap">
                {t(($) => $.anchoring.manage.status)}
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {report.takers.length > 0 ? (
              report.takers.map((taker) => (
                <TableRow key={taker.userId}>
                  <TableCell className="max-w-0">
                    <div className="min-w-0">
                      <div className="truncate text-sm font-medium">
                        {taker.firstName} {taker.lastName}
                      </div>
                      <div
                        className="text-muted-foreground truncate text-sm"
                        title={taker.email ?? undefined}
                      >
                        {taker.email}
                      </div>
                    </div>
                  </TableCell>
                  <TableCell className="whitespace-nowrap">
                    <Badge variant={taker.completed ? "success" : "secondary"}>
                      {taker.completed
                        ? t(($) => $.anchoring.manage.completed)
                        : t(($) => $.anchoring.manage.notCompleted)}
                    </Badge>
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell
                  colSpan={2}
                  className="text-muted-foreground text-center italic"
                >
                  {t(($) => $.anchoring.manage.noTakers)}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </Card>
    </div>
  );
};
